/**
 * NeighborhoodTour — Weidner Via6 day/night POI carousel.
 *
 * Lives on the /neighborhood page between the intro DividerImage and
 * the NeighborhoodMap. Mirrors the editorial "tour the neighborhood"
 * block on https://www.via6living.com/neighborhood:
 *
 *   - Section heading "Tour the neighborhood" (left-aligned, with the
 *     keyword "neighborhood" rendered in the brand cursive accent).
 *   - A connected two-tab toggle pill: ☀ BY DAY  |  🌙 BY NIGHT. The
 *     active half is filled with the brand secondary color; the
 *     inactive half is transparent with a thin border.
 *   - A horizontal carousel of POI cards. Each card shows the POI's
 *     featured photo, its name, and its short description — the same
 *     three fields the source site uses (`nei_img`, `nei_name`,
 *     `nei_desc`). On desktop 3 cards are visible; on mid sizes 2;
 *     on mobile 1.
 *   - Prev/Next arrows at the bottom-right of the carousel for
 *     keyboard / pointer paging.
 *
 * Data source: the standard `neighborhood_pois` property collection
 * (already populated for the NeighborhoodMap below this section). The
 * source via6living.com page maintains a custom `filter_type=day|night`
 * field on each POI to split the two carousels — vibesites' POI schema
 * doesn't carry that field, so we approximate it client-side by
 * matching the POI's `poi_type` against a small list of nightlife-style
 * categories (bar / lounge / nightlife / club / entertainment). Any POI
 * whose type contains one of those keywords falls into the BY NIGHT
 * carousel; everything else (restaurant, coffee, shopping, park, etc.)
 * stays in BY DAY. The classification is intentionally tolerant
 * (case-insensitive substring match) so editor-typed values like
 * "Bars & Lounges" or "Nightlife Spot" both resolve correctly.
 *
 * Background = theme primary; cards = white-on-primary so they read
 * as bright tiles lifted off the dark band, matching the screenshot.
 */

import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  useProperty,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ---------------------------------------------------------------------------
// Color helpers — duplicated from NeighborhoodMap so the night-card tint can
// match the accordion's lightened-primary surface exactly. We round-trip the
// primary brand color through HSL and bump the lightness channel by 5
// percentage points (the same `lighten(primaryColor, 5)` rule the
// NeighborhoodMap accordion uses), so the night cards read as the same
// "one tier brighter than the section bg" surface as the accordion.
// ---------------------------------------------------------------------------
function hueToHex(hue: number, sat: number, light: number): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { h: 200, s: 50, l: 50 };
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function lighten(hex: string, amount: number): string {
  const { h, s, l } = hexToHsl(hex);
  const nextL = Math.min(100, Math.max(0, l + amount));
  return hueToHex(h, s, nextL);
}

// CTA slot definition for the "Discover" intro block that sits above the
// day/night carousel. Default points at /floor-plans — the same
// cross-page nudge the rest of the theme uses ("explore the
// neighborhood, now go see a home").
const discoverCtaDef = {
  label: 'Discover CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// Mirrors the POI shape used by NeighborhoodMap. We only consume a
// subset (name / description / image / type) but keep the broader
// interface in scope so a future tweak can surface walk/drive time on
// the card without re-typing the binding.
interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_short_description?: string;
  poi_featured_image?: string;
}

/** Property data sometimes stringifies missing values as `"false"` /
 *  `"null"` / `"undefined"`. Treat those as empty so cards don't print
 *  the literal word. Same guard NeighborhoodMap / Reviews use. */
function hasValue(val: unknown): val is string {
  if (val == null || typeof val === 'boolean') return false;
  const s = String(val).trim();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

type Slide = 'day' | 'night';

// Keyword set used to bucket a POI as "night" rather than "day". Case
// insensitive substring match — see file header for rationale.
const NIGHT_KEYWORDS = ['bar', 'lounge', 'nightlife', 'club', 'entertainment'];

function isNightPoi(poi: POI): boolean {
  const t = (poi.poi_type || '').toLowerCase();
  return NIGHT_KEYWORDS.some((kw) => t.includes(kw));
}

// Inline icon glyphs for the toggle pill. Kept as components so the
// `currentColor` fill follows the pill's text color when it flips
// between active (filled) and inactive (outline) states.
function SunIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export function NeighborhoodTour() {
  const byDayLabel = useContentToken('neighborhoodtour.by_day_label');
  const byNightLabel = useContentToken('neighborhoodtour.by_night_label');
  const discoverCta = useSlotGroup('neighborhoodtour.discover_cta', discoverCtaDef);
  const pois = useCollectionBinding<POI>('neighborhoodtour.pois', {
    collection: 'neighborhood_pois',
  });

  // Property primary brand color — the section background and the
  // derived "one tier lighter" night-card tint both come from here so
  // the night tiles match the accordion surface on /neighborhood
  // regardless of which property is rendering the theme.
  const property = useProperty();
  const primaryColor = (property?.property_primary_color as string) || '#0a1f2e';
  const nightCardBg = useMemo(() => lighten(primaryColor, 5), [primaryColor]);

  const { dayPois, nightPois } = useMemo(() => {
    const day: POI[] = [];
    const night: POI[] = [];
    for (const poi of pois) {
      if (isNightPoi(poi)) night.push(poi);
      else day.push(poi);
    }
    return { dayPois: day, nightPois: night };
  }, [pois]);

  const [activeSlide, setActiveSlide] = useState<Slide>('day');
  const dayActive = activeSlide === 'day';
  const nightActive = activeSlide === 'night';

  const visiblePois = dayActive ? dayPois : nightPois;

  // Scroll-based carousel. We use a horizontally-scrolling flex track
  // (with CSS scroll-snap) rather than a JS-driven transform so the
  // experience falls back gracefully on touch — users can swipe
  // naturally, and the arrows are an enhancement that snaps to the
  // next "page" of cards.
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateEdgeState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft < maxScroll - 4);
  }, []);

  // Recompute edge state when the visible set changes (tab swap) and on
  // window resize. The "1 frame later" timeout lets the new tab's
  // cards lay out before we measure scrollWidth.
  useEffect(() => {
    const id = window.setTimeout(updateEdgeState, 0);
    return () => window.clearTimeout(id);
  }, [activeSlide, visiblePois.length, updateEdgeState]);

  useEffect(() => {
    window.addEventListener('resize', updateEdgeState);
    return () => window.removeEventListener('resize', updateEdgeState);
  }, [updateEdgeState]);

  const scrollByPage = useCallback((direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    // Page size = visible width minus a small overlap so the user
    // doesn't lose sight of the card that was on the edge.
    const page = Math.max(el.clientWidth - 32, 200);
    el.scrollBy({ left: page * direction, behavior: 'smooth' });
  }, []);

  const switchTo = (slide: Slide) => {
    setActiveSlide(slide);
    // Reset the scroll position so the new tab opens from the start
    // instead of mid-track.
    const el = trackRef.current;
    if (el) el.scrollLeft = 0;
  };

  return (
    <section
      className="pt-16 md:pt-24 pb-10 md:pb-14 bg-[var(--vibe-color-primary)] text-[var(--vibe-color-text-on-primary)]"
      // Expose the lightened-primary tint as a CSS variable so the
      // night-card rule (and any future variant) can pick it up without
      // an inline style on every card.
      style={{ ['--nt-night-card-bg' as any]: nightCardBg }}
    >
      <div className="max-w-7xl xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        {/* "Discover [city]" intro block. Sits above the editorial
            "Tour the neighborhood" heading (below the page's divider
            image) — gives the section a brochure-style lead-in with a
            centered headline, a paragraph of supporting copy, and a
            cross-page CTA. The headline pulls the property's city via
            a field token so the default reads naturally on any
            deployment ("Discover downtown Seattle", "Discover downtown
            Austin", etc.) without an editor override. */}
        <div className="text-center">
          <RichTextContent
            path="neighborhoodtour.discover_heading"
            defaultValue={
              'Discover <span class="vibe-cursive-accent">downtown</span> <span data-type="field-token" data-field="property_address_city"></span>'
            }
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal leading-tight tracking-tight [&_strong]:font-normal [&_b]:font-normal"
          />
          <RichTextContent
            path="neighborhoodtour.discover_description"
            defaultValue={
              '<p>For your next adventure, explore local attractions, parks, and dining within easy reach of <span data-type="field-token" data-field="property_name"></span>. Whether you\'re cheering on the home team, catching a show, or wandering the waterfront, endless experiences await right outside your front door.</p>'
            }
            as="div"
            className="mt-6 mx-auto max-w-3xl font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text-on-primary)] opacity-90 [&_a]:underline [&_p]:mt-3 first:[&_p]:mt-0 empty:hidden"
          />
          <div className="mt-8 flex justify-center">
            <EditableSlotGroup
              groupPath="neighborhoodtour.discover_cta"
              definition={discoverCtaDef}
            >
              {/* Site-wide outlined CTA pill, matching the editorial
                  reference (transparent fill, white border, white
                  label that inverts on hover). */}
              <a
                href={discoverCta.url || '/floor-plans'}
                target={discoverCta._target || '_self'}
                className="inline-block text-center w-[18rem] max-w-full px-6 py-2.5 text-sm font-medium tracking-[0.2em] uppercase rounded-full bg-transparent text-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-text-on-primary)] hover:bg-white hover:text-[var(--vibe-color-secondary)] hover:border-white focus-visible:bg-white focus-visible:text-[var(--vibe-color-secondary)] focus-visible:border-white transition-colors duration-150 ease-out"
              >
                {discoverCta.label || 'View Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        {/* Heading + optional description.
            Left-aligned to match the editorial reference on the source
            site. Description is OFF by default (empty richtext) so the
            heading sits tight against the tab pill — editors can fill
            it in if they want supporting copy. Heading sized one step
            smaller than a typical section title so it reads as a
            sub-section under the centered "Discover" lead-in above. */}
        <RichTextContent
          path="neighborhoodtour.heading"
          defaultValue={
            'Tour the <span class="vibe-cursive-accent">neighborhood</span>'
          }
          as="h2"
          className="mt-16 md:mt-20 font-heading text-2xl sm:text-3xl md:text-4xl font-normal leading-tight tracking-tight [&_strong]:font-normal [&_b]:font-normal"
        />
        <RichTextContent
          path="neighborhoodtour.description"
          as="div"
          className="mt-4 max-w-2xl font-body text-base sm:text-lg leading-relaxed text-[var(--vibe-color-text-on-primary)] opacity-90 [&_a]:underline [&_p]:mt-3 first:[&_p]:mt-0 empty:hidden"
        />

        {/* Two-tab toggle pill. The two buttons share a single rounded
            border so visually they read as one segmented control (vs
            two free-floating pills). Active half = filled secondary;
            inactive half = transparent with the same border color so
            the seam between halves stays subtle. */}
        <div
          className="mt-8 nt-toggle"
          role="tablist"
          aria-label="Time of day"
        >
          <button
            type="button"
            role="tab"
            aria-selected={dayActive}
            aria-controls="neighborhoodtour-panel-day"
            tabIndex={dayActive ? 0 : -1}
            className={`nt-toggle-btn${dayActive ? ' is-active' : ''}`}
            onClick={() => switchTo('day')}
          >
            <SunIcon />
            <EditableToken path="neighborhoodtour.by_day_label" as="span">
              {byDayLabel}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={nightActive}
            aria-controls="neighborhoodtour-panel-night"
            tabIndex={nightActive ? 0 : -1}
            className={`nt-toggle-btn${nightActive ? ' is-active' : ''}`}
            onClick={() => switchTo('night')}
          >
            <MoonIcon />
            <EditableToken path="neighborhoodtour.by_night_label" as="span">
              {byNightLabel}
            </EditableToken>
          </button>
        </div>

        {/* Carousel panel. We only render the active tab's cards (vs
            keeping both panels mounted) so the horizontal scroll state
            stays clean — the inactive set would otherwise hold its own
            scrollLeft offset and the next switchTo() reset wouldn't
            target the visible track. */}
        <div
          id={`neighborhoodtour-panel-${activeSlide}`}
          role="tabpanel"
          aria-label={dayActive ? 'Neighborhood by day' : 'Neighborhood by night'}
          // data-slide drives the day/night-specific card styling below
          // (white-surface vs lightened-primary surface) so we can keep
          // a single card template and let CSS pick the right palette.
          data-slide={activeSlide}
          className="mt-8 nt-carousel"
        >
          {visiblePois.length === 0 ? (
            <div className="py-12 text-center opacity-80">
              <p className="font-body text-base">
                No {dayActive ? 'daytime' : 'nighttime'} spots have been added
                yet.
              </p>
            </div>
          ) : (
            <>
              <div
                ref={trackRef}
                className="nt-track"
                onScroll={updateEdgeState}
              >
                {visiblePois.map((poi, i) => {
                  const img = hasValue(poi.poi_featured_image) ? poi.poi_featured_image : '';
                  const name = hasValue(poi.poi_name) ? poi.poi_name : '';
                  const desc = hasValue(poi.poi_short_description)
                    ? poi.poi_short_description
                    : '';
                  return (
                    <article key={`${activeSlide}-${i}-${name}`} className="nt-card">
                      <div className="nt-card-image">
                        {img ? (
                          <VibesitesImage
                            url={img}
                            alt={name}
                            className="block w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-[var(--vibe-color-text)]/10" />
                        )}
                      </div>
                      <div className="nt-card-body">
                        {name ? <h3 className="nt-card-title">{name}</h3> : null}
                        {desc ? <p className="nt-card-desc">{desc}</p> : null}
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Nav arrows. Disabled when the track is at the
                  corresponding edge — the arrows fade rather than
                  vanish so the carousel's right side stays anchored. */}
              <div className="nt-nav">
                <button
                  type="button"
                  className="nt-nav-btn"
                  aria-label="Previous"
                  disabled={!canPrev}
                  onClick={() => scrollByPage(-1)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="nt-nav-btn"
                  aria-label="Next"
                  disabled={!canNext}
                  onClick={() => scrollByPage(1)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        /* Cursive accent rule for the heading's secondary-script word.
           Matches the same treatment used across PropertyOverview /
           InteriorIntro / etc. */
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }

        /* ============================================================
           Segmented day/night toggle pill.
           The outer pill is a solid white container that sits on the
           dark primary band — matches the editorial reference where
           the toggle reads as a bright surface lifted off the page.
           The active half is filled with the brand secondary color
           (white label/icon on top); the inactive half stays
           transparent so the white container shows through and the
           label/icon render in a muted dark tone.
           ============================================================ */
        .nt-toggle {
          display: inline-flex;
          align-items: stretch;
          border: none;
          border-radius: 9999px;
          padding: 4px;
          background: #fff;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        .nt-toggle-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.625rem 1.5rem;
          border-radius: 9999px;
          border: none;
          background: transparent;
          /* Inactive label/icon color — a muted neutral that reads on
             the white container without competing with the active
             secondary fill. */
          color: #6b7280;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          cursor: pointer;
          transition:
            background-color 0.2s ease-out,
            color 0.2s ease-out;
        }
        .nt-toggle-btn:hover {
          color: var(--vibe-color-secondary);
          background: transparent;
        }
        .nt-toggle-btn.is-active {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .nt-toggle-btn.is-active:hover {
          /* Keep the active fill stable on hover so the segmented look
             doesn't flicker between hover/active styling. */
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .nt-toggle-btn:focus-visible {
          outline: 2px solid var(--vibe-color-secondary);
          outline-offset: 2px;
        }
        .nt-toggle-btn svg {
          flex-shrink: 0;
        }

        /* ============================================================
           Carousel container — relative-positioned so the arrows can be
           absolutely placed in the bottom-right corner of this block.
           ============================================================ */
        .nt-carousel {
          position: relative;
        }

        /* Horizontal scroll track. flex+overflow-x:auto with scroll-snap
           gives swipe-friendly behavior; the JS arrows snap by ~one
           viewport width. Scrollbar hidden so the only horizontal
           affordance is the snap-+-arrows behavior, matching the
           editorial look (no visible scrollbar under the cards). */
        .nt-track {
          display: flex;
          gap: 1.5rem;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          /* Hide scrollbar (Firefox, IE, modern). */
          scrollbar-width: none;
          padding-bottom: 0.5rem;
        }
        .nt-track::-webkit-scrollbar {
          display: none;
        }

        /* ============================================================
           Card. Day cards keep the white "framed photo" surface lifted
           off the dark primary band — both title and description use
           text-on-secondary (with black fallback) so they stay legible
           against the white card.

           Night cards (data-slide="night" on the carousel) swap the
           white surface for the same lightened-primary tint the
           NeighborhoodMap accordion uses, so the two sections read as
           the same "one tier brighter than the section bg" surface.
           Title + description shift to text-on-primary so they keep
           readable contrast on the dark-ish night tile.
           ============================================================ */
        .nt-card {
          flex: 0 0 auto;
          width: calc(100% - 0.5rem);
          background: #fff;
          color: var(--vibe-color-text-on-secondary, #000);
          border-radius: 10px;
          overflow: hidden;
          scroll-snap-align: start;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }
        .nt-carousel[data-slide="night"] .nt-card {
          background: var(--nt-night-card-bg, var(--vibe-color-primary));
          color: var(--vibe-color-text-on-primary, #fff);
        }
        @media (min-width: 640px) {
          /* Two cards per page at sm/md. */
          .nt-card { width: calc(50% - 0.75rem); }
        }
        @media (min-width: 1024px) {
          /* Three cards per page at desktop. */
          .nt-card { width: calc(33.333% - 1rem); }
        }
        @media (min-width: 1440px) {
          /* Four cards per page on wide viewports, matching the
             screenshot's 4-up layout. */
          .nt-card { width: calc(25% - 1.125rem); }
        }

        .nt-card-image {
          /* Inset the image so the white card surface shows around it
             on the top and sides — matches the editorial "framed photo"
             look where the card reads as a paper mat around the image. */
          margin: 0.75rem 0.75rem 0;
          aspect-ratio: 4 / 3;
          /* Light neutral placeholder so a missing photo still reads
             as part of the white card surface rather than a hole. */
          background: #f4f4f4;
          border-radius: 6px;
          overflow: hidden;
        }
        .nt-card-body {
          /* Horizontal padding matches the image's outer edge (0.75rem
             margin + ~0.25rem) so the title left-aligns with the photo. */
          padding: 1rem 1rem 1.25rem;
        }
        .nt-card-title {
          font-family: var(--vibe-font-body);
          font-size: 1.0625rem;
          font-weight: 700;
          line-height: 1.25;
          margin: 0;
          color: var(--vibe-color-text-on-secondary, #000);
        }
        /* Night-tile title needs to lift off the lightened-primary
           surface — text-on-secondary would render the same brand-light
           tone the day cards use and lose contrast. */
        .nt-carousel[data-slide="night"] .nt-card-title {
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .nt-card-desc {
          margin: 0.5rem 0 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.5;
          /* Day cards: description uses text-on-secondary so it picks
             up the brand-derived "copy on a light surface" color the
             white card was designed for. Night cards override this
             below to text-on-primary so the description stays legible
             against the lightened-primary surface. */
          color: var(--vibe-color-text-on-secondary, #000);
          opacity: 0.85;
          /* Clamp descriptions to 3 lines so card heights stay aligned
             across the row even when copy lengths vary. */
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        /* Night-tile description: lift off the lightened-primary
           surface with text-on-primary. */
        .nt-carousel[data-slide="night"] .nt-card-desc {
          color: var(--vibe-color-text-on-primary, #fff);
        }

        /* ============================================================
           Nav arrows. Bottom-right of the carousel block — kept inside
           the carousel container so they stay aligned to the cards
           regardless of section padding. Round outlined buttons matching
           the editorial reference; disabled state fades to 30% opacity.
           ============================================================ */
        .nt-nav {
          display: flex;
          justify-content: flex-end;
          gap: 0.65rem;
          margin-top: 1.25rem;
        }
        .nt-nav-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 9999px;
          border: 1px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          color: var(--vibe-color-text-on-primary, #fff);
          cursor: pointer;
          transition:
            opacity 0.15s ease-out,
            background-color 0.15s ease-out,
            color 0.15s ease-out;
        }
        .nt-nav-btn:hover:not(:disabled) {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: #fff;
        }
        .nt-nav-btn:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #fff);
          outline-offset: 2px;
        }
        .nt-nav-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodTour',
  displayName: 'Neighborhood Tour (Day / Night)',
  tokens: {
    'neighborhoodtour.discover_heading': {
      label: 'Discover Heading',
      type: 'richtext',
      defaultValue:
        'Discover <span class="vibe-cursive-accent">downtown</span> <span data-type="field-token" data-field="property_address_city"></span>',
    },
    'neighborhoodtour.discover_description': {
      label: 'Discover Description',
      type: 'richtext',
      defaultValue:
        '<p>For your next adventure, explore local attractions, parks, and dining within easy reach of <span data-type="field-token" data-field="property_name"></span>. Whether you\'re cheering on the home team, catching a show, or wandering the waterfront, endless experiences await right outside your front door.</p>',
    },
    'neighborhoodtour.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        'Tour the <span class="vibe-cursive-accent">neighborhood</span>',
    },
    'neighborhoodtour.description': {
      label: 'Description (optional)',
      type: 'richtext',
      defaultValue: '',
    },
    'neighborhoodtour.by_day_label': {
      label: 'By Day Tab Label',
      type: 'text',
      defaultValue: 'By Day',
    },
    'neighborhoodtour.by_night_label': {
      label: 'By Night Tab Label',
      type: 'text',
      defaultValue: 'By Night',
    },
  },
  slotGroups: {
    'neighborhoodtour.discover_cta': {
      label: 'Discover CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Link Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
  collectionBindings: {
    'neighborhoodtour.pois': {
      label: 'Neighborhood Points of Interest',
      collection: 'neighborhood_pois',
      description:
        'Pulled from the property neighborhood POI list. POIs whose type contains "bar", "lounge", "nightlife", "club", or "entertainment" appear on the BY NIGHT tab; all others appear on the BY DAY tab.',
    },
  },
};

(NeighborhoodTour as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodTour;
